import { Suspense, lazy } from 'react';
import { Navigate } from 'react-router-dom';
import { useSession } from '../auth/client.ts';

/**
 * Root canvas route.
 *
 * Spec §11.3 lets the kid play DURING the 24-hour grace window without a
 * verified parent account — that's the whole point of email-plus-VPC.
 * So the gate here is "do we have a session at all?" not "is the email
 * verified?". A pending parent_account whose grace window has expired is
 * cleared by the api purge cron (services/cron.ts), which kills the
 * session — at which point useSession returns no data and we redirect.
 *
 * The canvas component is lazy-loaded so parents on /signup/* don't pay
 * for the three.js bundle (~270 KB gzipped) on their first paint —
 * they only need the React + router + form code (~25 KB).
 */
const ExplorersCanvas = lazy(() =>
  import('../canvas/ExplorersCanvas.tsx').then((m) => ({ default: m.ExplorersCanvas })),
);

function CanvasFallback(): JSX.Element {
  return (
    <div className="grid h-dvh place-items-center bg-explorers-bg text-slate-500">Loading…</div>
  );
}

export function Root(): JSX.Element {
  const { data: session, isPending } = useSession();

  if (isPending) {
    return <div className="grid h-dvh place-items-center bg-amber-50 text-slate-500">Loading…</div>;
  }

  if (!session) return <Navigate to="/signup/1" replace />;

  return (
    <Suspense fallback={<CanvasFallback />}>
      <ExplorersCanvas />
    </Suspense>
  );
}
